import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, getCategoryColor } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search,
  Plus,
  ClipboardList,
  Stethoscope,
  FileText,
  Pill,
  TestTube,
  Scan,
  LogOut,
  Pencil,
  Loader2,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'registration', label: 'Registration', icon: ClipboardList },
  { value: 'consultation', label: 'Consultation', icon: Stethoscope },
  { value: 'progress', label: 'Progress Note', icon: FileText },
  { value: 'prescription', label: 'Prescription', icon: Pill },
  { value: 'lab', label: 'Lab Order', icon: TestTube },
  { value: 'imaging', label: 'Imaging', icon: Scan },
  { value: 'discharge', label: 'Discharge', icon: LogOut },
];

export default function FormsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');

  useEffect(() => {
    fetchTemplates();
  }, [categoryFilter]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      
      const response = await formsAPI.getTemplates(params);
      setTemplates(response.data);
    } catch (err) {
      toast.error('Failed to fetch form templates');
    }
    setLoading(false);
  };

  const handleCategoryChange = (value) => {
    setCategoryFilter(value);
    setSearchParams(prev => {
      if (value) prev.set('category', value);
      else prev.delete('category');
      return prev;
    });
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.icon || FileText;
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="forms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Forms Library</h1>
          <p className="text-muted-foreground mt-1">
            Manage and use medical form templates
          </p>
        </div>
        {hasPermission('forms:create') && (
          <Button onClick={() => navigate('/forms/builder')} className="gap-2" data-testid="create-form-btn">
            <Plus className="h-4 w-4" />
            Create Form
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search forms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="form-search"
              />
            </div>
            <Select value={categoryFilter || "all"} onValueChange={(v) => handleCategoryChange(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={!categoryFilter ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => handleCategoryChange("all")}
          className="rounded-full"
        >
          All
        </Button>
        {CATEGORIES.map(cat => (
          <Button
            key={cat.value}
            variant={categoryFilter === cat.value ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleCategoryChange(cat.value)}
            className="gap-2 rounded-full"
            data-testid={`category-pill-${cat.value}`}
          >
            <cat.icon className="h-4 w-4" />
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
            <h3 className="text-lg font-medium mb-1">No forms found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || categoryFilter
                ? 'Try adjusting your search or filters'
                : 'Create your first form template to get started'}
            </p>
            {hasPermission('forms:create') && !searchQuery && !categoryFilter && (
              <Button onClick={() => navigate('/forms/builder')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Form
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => {
            const IconComponent = getCategoryIcon(template.category);
            return (
              <Card
                key={template.id}
                className="card-interactive cursor-pointer"
                onClick={() => navigate(`/forms/submit/${template.id}`)}
                data-testid={`form-card-${template.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className={cn(
                      'w-12 h-12 rounded-xl flex items-center justify-center',
                      getCategoryColor(template.category)
                    )}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                    <Badge variant="secondary" className={cn('text-xs', getCategoryColor(template.category))}>
                      {template.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg mt-4">{template.name}</CardTitle>
                  {template.description && (
                    <CardDescription className="line-clamp-2">
                      {template.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{template.fields?.length || 0} fields</span>
                    <span>Updated {formatDate(template.updated_at)}</span>
                  </div>
                  
                  {hasPermission('forms:update') && (
                    <div className="mt-4 pt-4 border-t flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/forms/builder/${template.id}`);
                        }}
                        data-testid={`edit-form-${template.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/forms/submit/${template.id}`);
                        }}
                        data-testid={`use-form-${template.id}`}
                      >
                        Use Form
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
